import {
  getNonEmptyRows,
  isFieldEmpty,
  isValidAgainstPattern,
  isValidAgainstRegexp,
  isValidAlpha,
  isValidAlphanumeric,
  isValidDate,
  isValidEmail,
  isValidNumber,
  isValidTime,
  isValidUrl,
} from "$lib/helpers/validate";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);

export const computeFormValidation = async (
  fieldDisplay: FieldDisplay[],
  pageDisplay: PageDisplay[],
  formValues: FormValues,
  formMeta: FormMeta,
) => {
  let parsePhoneNumber: any;
  let getFreeEmail: any;

  for (let field of fieldDisplay) {
    // Skip fields that don't require validation
    if (field.type === "Hidden") continue;
    if (field.type === "RichText") continue;
    if (field.type === "CreditCard") continue;

    const fieldValue = formValues[field.id];
    const page = pageDisplay.find((page) => page.id === field.page);

    // Reset validation
    field.valid = true;
    field.empty = isFieldEmpty(fieldValue);
    field.errorType = null;

    // Skip validation if field is not visible
    if (!field.visibleComputed || !page.visibleComputed) {
      continue;
    }

    // Validate as ChoiceValue[]
    const is_required = field.requiredComputed ?? field.settings.required;
    if (is_required) {
      if (isFieldEmpty(fieldValue)) {
        field.valid = false;
        field.errorType = "required";
      }
    } else {
      if (isFieldEmpty(fieldValue)) {
        continue;
      }
    }

    if (
      field.type === "Address" && is_required && typeof fieldValue === "object"
    ) {
      const fieldValueAddress = fieldValue as Address;
      if (
        !fieldValueAddress.line1 ||
        !fieldValueAddress.city ||
        !fieldValueAddress.state ||
        !fieldValueAddress.postal_code ||
        !fieldValueAddress.country
      ) {
        field.valid = false;
        field.errorType = "required";
      }
    }

    if (field.type === "Comment" && typeof fieldValue === "string") {
      const minChars = parseInt(field.settings.minChars) ?? null;
      const maxChars = parseInt(field.settings.maxChars) ?? null;
      if (
        minChars && fieldValue.length < minChars
      ) {
        field.valid = false;
        field.errorType = "min_chars";
        field.errorNumber = minChars;
      }
      if (
        maxChars && fieldValue.length > maxChars
      ) {
        field.valid = false;
        field.errorType = "max_chars";
        field.errorNumber = maxChars;
      }
    }

    if (field.type === "Phone" && typeof fieldValue === "string") {
      if (!parsePhoneNumber) {
        parsePhoneNumber =
          (await import("libphonenumber-js/min")).parsePhoneNumber;
      }
      let parsed;
      try {
        parsed = parsePhoneNumber(fieldValue);
      } catch (e) {
      }
      if (!parsed || !parsed.isValid()) {
        field.valid = false;
        field.errorType = "invalid";
      }
    }

    if (field.type === "FileUpload" && fieldValue instanceof Array) {
      if (field.settings?.min) {
        const min = parseInt(field.settings.min);
        if ((fieldValue as Upload[]).length < min) {
          field.valid = false;
          field.errorType = "min_required";
          field.errorNumber = min;
        }
      }
      if (field.settings?.max) {
        const max = parseInt(field.settings.max);
        if ((fieldValue as Upload[]).length > max) {
          field.valid = false;
          field.errorType = "max_allowed";
          field.errorNumber = max;
        }
      }
    }

    if (field.type === "MultipleChoice" && Array.isArray(fieldValue)) {
      const myField = field as MultipleChoiceField;
      const min = myField.settings.min ? parseInt(myField.settings.min) : null;
      const max = myField.settings.max ? parseInt(myField.settings.max) : null;
      if (min && fieldValue.length < min && field.settings.multiple) {
        field.valid = false;
        field.errorType = "min_required";
        field.errorNumber = min;
      }
      if (max && fieldValue.length > max && field.settings.multiple) {
        field.valid = false;
        field.errorType = "max_allowed";
        field.errorNumber = max;
      }
    }

    if (field.type === "Dropdown" && Array.isArray(fieldValue)) {
      const myField = field as DropdownField;
      const min = myField.settings.min ? parseInt(myField.settings.min) : null;
      const max = myField.settings.max ? parseInt(myField.settings.max) : null;
      if (min && fieldValue.length < min && field.settings.multiple) {
        field.valid = false;
        field.errorType = "min_required";
        field.errorNumber = min;
      }
      if (max && fieldValue.length > max && field.settings.multiple) {
        field.valid = false;
        field.errorType = "max_allowed";
        field.errorNumber = max;
      }
    }

    // Validate one line input
    if (field.type === "OneLineInput" && typeof fieldValue === "string") {
      if (field.settings.validation && !isFieldEmpty(fieldValue)) {
        if (field.settings.validation === "url") {
          if (!isValidUrl(fieldValue)) {
            field.valid = false;
            field.errorType = "invalid_url";
          }
        }
        if (field.settings.validation === "digits") {
          if (!isValidNumber(fieldValue)) {
            field.valid = false;
            field.errorType = "invalid_numeric";
          }
        }
        if (field.settings.validation === "numeric") {
          if (!isValidNumber(fieldValue, true)) {
            field.valid = false;
            field.errorType = "invalid_decimal";
          }
        }
        if (field.settings.validation === "alpha") {
          if (!isValidAlpha(fieldValue)) {
            field.valid = false;
            field.errorType = "invalid";
          }
        }
        if (field.settings.validation === "alphanumeric") {
          if (!isValidAlphanumeric(fieldValue)) {
            field.valid = false;
            field.errorType = "invalid";
          }
        }
        if (field.settings.validation === "regexp" && field.settings.regexp) {
          if (!isValidAgainstRegexp(fieldValue, field.settings.regexp)) {
            field.valid = false;
            field.errorType = "invalid";
          }
        }
        if (field.settings.validation === "pattern" && field.settings.pattern) {
          if (!isValidAgainstPattern(fieldValue, field.settings.pattern)) {
            field.valid = false;
            field.errorType = "invalid";
          }
        }
      }
      const minChars = parseInt(field.settings.minChars) ?? null;
      const maxChars = parseInt(field.settings.maxChars) ?? null;
      if (
        minChars && fieldValue.length < minChars
      ) {
        field.valid = false;
        field.errorType = "min_chars";
        field.errorNumber = minChars;
      }
      if (
        maxChars && fieldValue.length > maxChars
      ) {
        field.valid = false;
        field.errorType = "max_chars";
        field.errorNumber = maxChars;
      }
      if (
        field.settings.validation === "digits" ||
        field.settings.validation === "numeric"
      ) {
        const minValue = field.settings.minValue
          ? parseFloat(field.settings.minValue)
          : null;
        const maxValue = field.settings.maxValue
          ? parseFloat(field.settings.maxValue)
          : null;
        const value = parseFloat(fieldValue);
        if (minValue !== null && value < minValue) {
          field.valid = false;
          field.errorType = "min_value";
          field.errorNumber = minValue;
        }
        if (maxValue !== null && value > maxValue) {
          field.valid = false;
          field.errorType = "max_value";
          field.errorNumber = maxValue;
        }
      }
    }

    // Dont validate further if field is empty
    if (isFieldEmpty(fieldValue)) {
      continue;
    }

    // Validate date field - make sure it's in ISO format
    if (field.type === "Date" && typeof fieldValue === "string") {
      if (!isValidDate(fieldValue)) {
        field.valid = false;
        field.errorType = "invalid";
      }
      if (
        field.settings.minComputed &&
        dayjs(fieldValue).diff(field.settings.minComputed, "days") < 0
      ) {
        field.valid = false;
        field.errorType = "invalid";
      }
      if (
        field.settings.maxComputed &&
        dayjs(fieldValue).diff(field.settings.maxComputed, "days") > 0
      ) {
        field.valid = false;
        field.errorType = "invalid";
      }
      if (
        field.settings.allowedDays &&
        field.settings.allowedDays[dayjs(fieldValue).format("d") as any] ===
          false
      ) {
        field.valid = false;
        field.errorType = "invalid";
      }
    }

    // Validate email field
    if (field.type === "Email" && typeof fieldValue === "string") {
      if (!isValidEmail(fieldValue)) {
        field.valid = false;
        field.errorType = "invalid";
      }
      if (field.settings.disableFree) {
        if (!getFreeEmail) {
          getFreeEmail = (await import("$lib/data/form")).getFreeEmail;
        }
        const emailDomain = fieldValue.split("@")[1];
        if (emailDomain && getFreeEmail().includes(emailDomain.toLowerCase())) {
          field.valid = false;
          field.errorType = "free_not_allowed";
        }
      }
    }

    // Validate email field
    if (field.type === "TableEntry") {
      const rows = (fieldValue as TableEntryValue).rows;
      const filled_rows = getNonEmptyRows(rows).length;
      const min = field.settings.min ? parseInt(field.settings.min) : null;
      const max = field.settings.max ? parseInt(field.settings.max) : null;
      if (min && filled_rows < min) {
        field.valid = false;
        field.errorType = "min_required";
        field.errorNumber = min;
      }
      if (max && filled_rows > max) {
        field.valid = false;
        field.errorType = "max_allowed";
        field.errorNumber = max;
      }
    }

    // Validate time field
    if (field.type === "Time" && typeof fieldValue === "string") {
      if (!isValidTime(fieldValue)) {
        field.valid = false;
        field.errorType = "invalid";
      }
      const valueTime = dayjs(fieldValue, "HH:mm");
      const validMin = dayjs(field.settings.min, "HH:mm");
      const validMax = dayjs(field.settings.max, "HH:mm");

      // Make sure time is within min and max
      if (
        field.settings.min && validMin.isValid() &&
        valueTime.isBefore(validMin)
      ) {
        field.valid = false;
        field.errorType = "invalid";
      }
      if (
        field.settings.max && validMax.isValid() &&
        valueTime.isAfter(validMax)
      ) {
        field.valid = false;
        field.errorType = "invalid";
      }
    }
  }
};
